/**
 * Shared date/time formatting utilities.
 */

/**
 * Format a date string or Date to a localized date string (no time).
 * Returns '-' for null/undefined input.
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '-';
  return new Date(date).toLocaleDateString();
}

/**
 * Format a date string or Date to a localized date+time string.
 * Returns '-' for null/undefined input.
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '-';
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format milliseconds to MM:SS.ms display (e.g., scoring timer).
 * Returns '-' for null/undefined input. Clamps negative values to 0.
 */
export function formatTime(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '-';
  if (ms < 0) ms = 0;
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const milliseconds = Math.floor((ms % 1000) / 10);
  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}.${milliseconds.toString().padStart(2, '0')}`;
}

/**
 * Format milliseconds with adaptive display:
 * - Under 1 minute: "X.XXXs" (3-digit ms)
 * - 1 minute or more: "M:SS.XXX" (3-digit ms)
 * Returns '-' for null/undefined input. Clamps negative values to 0.
 */
export function formatTimeDetailed(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '-';
  if (ms < 0) ms = 0;
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  const remainingMs = ms % 1000;
  if (minutes > 0) {
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}.${remainingMs.toString().padStart(3, '0')}`;
  }
  return `${remainingSeconds}.${remainingMs.toString().padStart(3, '0')}s`;
}

/**
 * Format a date as relative time (e.g., "just now", "yesterday", "3 days ago", or a short date).
 * Requires a translation function with keys: 'yesterday', 'daysAgo'.
 */
export function formatRelativeTime(
  dateStr: string,
  t: (key: string, values?: Record<string, unknown>) => string,
): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return t('yesterday');
  } else if (diffDays < 7) {
    return `${diffDays} ${t('daysAgo')}`;
  } else {
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
}
